import React, { useState, useEffect } from 'react';
import { format } from 'date-fns';
import ServiceSelector from '../components/booking/ServiceSelector';
import BookingCalendar from '../components/booking/BookingCalendar';
import TimeSlotSelector from '../components/booking/TimeSlotSelector';
import ClientInformationForm from '../components/booking/ClientInformationForm';
import BookingSummary from '../components/booking/BookingSummary';
import BookingConfirmation from '../components/booking/BookingConfirmation';
import { servicesAPI, bookingsAPI } from '../services/api';

const BookingPage = () => {
  // Step management
  const [currentStep, setCurrentStep] = useState(1);
  const [showConfirmation, setShowConfirmation] = useState(false);

  // Data state
  const [services, setServices] = useState([]);
  const [selectedService, setSelectedService] = useState(null);
  const [selectedDate, setSelectedDate] = useState(null);
  const [timeSlots, setTimeSlots] = useState([]);
  const [selectedTimeSlot, setSelectedTimeSlot] = useState(null);
  const [clientInfo, setClientInfo] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    notes: '',
  });
  const [confirmedBooking, setConfirmedBooking] = useState(null);

  // Loading states
  const [loadingServices, setLoadingServices] = useState(true);
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // Load services on mount
  useEffect(() => {
    const fetchServices = async () => {
      try {
        const response = await servicesAPI.getAll();
        setServices(response.data.services || mockServices);
      } catch (error) {
        console.error('Error fetching services:', error);
        // Use mock data if API fails
        setServices(mockServices);
      } finally {
        setLoadingServices(false);
      }
    };

    fetchServices();
  }, []);

  // Load time slots when date and service are selected
  useEffect(() => {
    const fetchTimeSlots = async () => {
      if (!selectedDate || !selectedService) {
        setTimeSlots([]);
        return;
      }

      setLoadingSlots(true);
      try {
        const dateStr = format(selectedDate, 'yyyy-MM-dd');
        const response = await bookingsAPI.getAvailableSlots(
          dateStr,
          selectedService.id
        );
        setTimeSlots(response.data.slots || generateMockTimeSlots(selectedDate));
      } catch (error) {
        console.error('Error fetching time slots:', error);
        // Use mock data if API fails
        setTimeSlots(generateMockTimeSlots(selectedDate));
      } finally {
        setLoadingSlots(false);
      }
    };

    fetchTimeSlots();
  }, [selectedDate, selectedService]);

  // Handle service selection
  const handleServiceSelect = (service) => {
    setSelectedService(service);
    setCurrentStep(2);
  };

  // Handle date selection
  const handleDateSelect = (date) => {
    setSelectedDate(date);
    setSelectedTimeSlot(null);
    setCurrentStep(3);
  };

  // Handle time slot selection
  const handleTimeSlotSelect = (slot) => {
    setSelectedTimeSlot(slot);
    setCurrentStep(4);
  };

  // Handle booking submission
  const handleSubmitBooking = async () => {
    setSubmitting(true);

    try {
      // Create booking
      const bookingData = {
        serviceId: selectedService.id,
        startTime: selectedTimeSlot.startTime,
        endTime: selectedTimeSlot.endTime,
        clientInfo: {
          firstName: clientInfo.firstName,
          lastName: clientInfo.lastName,
          email: clientInfo.email,
          phone: clientInfo.phone,
          notes: clientInfo.notes,
        },
      };

      const response = await bookingsAPI.create(bookingData);

      // Set confirmation data
      setConfirmedBooking({
        ...response.data.booking,
        serviceName: selectedService.name,
        duration: selectedService.duration,
        clientEmail: clientInfo.email,
      });

      setShowConfirmation(true);
    } catch (error) {
      console.error('Error creating booking:', error);
      alert('Failed to create booking. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  // Handle new booking
  const handleNewBooking = () => {
    setShowConfirmation(false);
    setCurrentStep(1);
    setSelectedService(null);
    setSelectedDate(null);
    setSelectedTimeSlot(null);
    setClientInfo({
      firstName: '',
      lastName: '',
      email: '',
      phone: '',
      notes: '',
    });
  };

  // Show confirmation page
  if (showConfirmation && confirmedBooking) {
    return (
      <BookingConfirmation
        booking={confirmedBooking}
        onNewBooking={handleNewBooking}
      />
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-4xl font-bold text-gray-900 mb-2">
            Book Your Appointment
          </h1>
          <p className="text-lg text-gray-600">
            Choose your service, select a time, and we'll take care of the rest
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Main Content - 2 columns */}
          <div className="lg:col-span-2 space-y-6">
            {/* Step 1: Service Selection */}
            <ServiceSelector
              services={services}
              selectedService={selectedService}
              onServiceSelect={handleServiceSelect}
              loading={loadingServices}
            />

            {/* Step 2: Date Selection */}
            {currentStep >= 2 && (
              <BookingCalendar
                selectedDate={selectedDate}
                onDateSelect={handleDateSelect}
              />
            )}

            {/* Step 3: Time Selection */}
            {currentStep >= 3 && selectedDate && (
              <TimeSlotSelector
                slots={timeSlots}
                selectedSlot={selectedTimeSlot}
                onSlotSelect={handleTimeSlotSelect}
                loading={loadingSlots}
              />
            )}

            {/* Step 4: Client Information */}
            {currentStep >= 4 && selectedTimeSlot && (
              <ClientInformationForm
                clientInfo={clientInfo}
                onClientInfoChange={setClientInfo}
                onSubmit={handleSubmitBooking}
                loading={submitting}
              />
            )}
          </div>

          {/* Sidebar - 1 column */}
          <div className="lg:col-span-1">
            <BookingSummary
              service={selectedService}
              date={selectedDate}
              timeSlot={selectedTimeSlot}
              clientInfo={clientInfo}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

// Mock data for development/fallback
const mockServices = [
  {
    id: 1,
    name: 'Swedish Massage',
    description: 'Relaxing full-body massage using gentle pressure to promote relaxation',
    duration: 60,
    price: 80,
  },
  {
    id: 2,
    name: 'Deep Tissue Massage',
    description: 'Therapeutic massage targeting deep layers of muscle and connective tissue',
    duration: 90,
    price: 110,
  },
  {
    id: 3,
    name: 'Hot Stone Massage',
    description: 'Soothing massage using heated stones to relieve tension and improve circulation',
    duration: 75,
    price: 95,
  },
];

const generateMockTimeSlots = (date) => {
  const slots = [];
  const baseDate = new Date(date);

  // Generate slots from 9 AM to 6 PM
  for (let hour = 9; hour < 18; hour++) {
    const startTime = new Date(baseDate);
    startTime.setHours(hour, 0, 0, 0);

    const endTime = new Date(baseDate);
    endTime.setHours(hour + 1, 0, 0, 0);

    slots.push({
      startTime: startTime.toISOString(),
      endTime: endTime.toISOString(),
      available: Math.random() > 0.3, // 70% available
    });
  }

  return slots;
};

export default BookingPage;
